/**
 * Миксин для работы с буфером обмена - вставка файлов
 * Поддерживает:
 * - Ctrl+V для вставки файлов/изображений
 * - Кнопку для программной вставки
 */
export default {
  data() {
    return {
      clipboardSupported: true
    }
  },
  methods: {
    /**
     * Обработчик события paste
     * @param {ClipboardEvent} event - событие paste
     * @param {Function} onFilesAdded - коллбэк для добавления файлов
     */
    handlePaste(event, onFilesAdded) {
      if (!event.clipboardData || !event.clipboardData.items) {
        console.warn('Clipboard API не поддерживается');
        return;
      }

      const items = event.clipboardData.items;
      const files = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Проверяем, является ли элемент файлом
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        // Предотвращаем вставку в textarea
        event.preventDefault();
        
        if (typeof onFilesAdded === 'function') {
          onFilesAdded(files);
        }
        
        this.$bvToast && this.$bvToast.toast(
          `Добавлено файлов: ${files.length}`,
          {
            title: 'Файлы из буфера',
            variant: 'success',
            solid: true,
            autoHideDelay: 2000
          }
        );
      }
    },

    /**
     * Обработчик для кнопки вставки из буфера
     * Работает через Clipboard API
     * @param {Function} onFilesAdded - коллбэк для добавления файлов
     */
    async handleClipboardButton(onFilesAdded) {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        this.$bvToast && this.$bvToast.toast(
          'Ваш браузер не поддерживает чтение из буфера обмена. Используйте Ctrl+V.',
          {
            title: 'Не поддерживается',
            variant: 'warning',
            solid: true
          }
        );
        return;
      }

      try {
        const clipboardItems = await navigator.clipboard.read();
        const files = [];

        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            // Проверяем, что это файл (изображение или другой тип)
            if (type.startsWith('image/') || type.startsWith('application/') || type.startsWith('video/') || type.startsWith('audio/')) {
              const blob = await clipboardItem.getType(type);
              
              // Создаем File объект из Blob
              const extension = this.getExtensionFromMimeType(type);
              const fileName = `clipboard_${Date.now()}${extension}`;
              const file = new File([blob], fileName, { type });
              
              files.push(file);
            }
          }
        }

        if (files.length > 0) {
          if (typeof onFilesAdded === 'function') {
            onFilesAdded(files);
          }
          
          this.$bvToast && this.$bvToast.toast(
            `Добавлено файлов: ${files.length}`,
            {
              title: 'Файлы из буфера',
              variant: 'success',
              solid: true,
              autoHideDelay: 2000
            }
          );
        } else {
          this.$bvToast && this.$bvToast.toast(
            'В буфере обмена нет файлов',
            {
              title: 'Информация',
              variant: 'info',
              solid: true
            }
          );
        }
      } catch (error) {
        console.error('Ошибка чтения из буфера обмена:', error);
        
        // Если ошибка связана с разрешениями
        if (error.name === 'NotAllowedError') {
          this.$bvToast && this.$bvToast.toast(
            'Доступ к буферу обмена запрещен. Разрешите доступ в настройках браузера или используйте Ctrl+V.',
            {
              title: 'Доступ запрещен',
              variant: 'warning',
              solid: true
            }
          );
        } else {
          this.$bvToast && this.$bvToast.toast(
            'Не удалось прочитать данные из буфера обмена. Используйте Ctrl+V.',
            {
              title: 'Ошибка',
              variant: 'danger',
              solid: true
            }
          );
        }
      }
    },

    /**
     * Получить расширение файла из MIME-типа
     * @param {string} mimeType - MIME-тип
     * @returns {string} расширение файла с точкой
     */
    getExtensionFromMimeType(mimeType) {
      const mimeMap = {
        'image/png': '.png',
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/jfif': '.jpg',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/svg+xml': '.svg',
        'image/bmp': '.bmp',
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/zip': '.zip',
        'application/x-rar-compressed': '.rar',
        'text/plain': '.txt',
        'video/mp4': '.mp4',
        'audio/mpeg': '.mp3'
      };

      return mimeMap[mimeType] || '.bin';
    }
  }
}

